import {useSelector} from "react-redux";
import moment from "moment";
import {TRANSACTION_TYPE_TRANSFER} from "../../codes";
import {useMemo} from "react";
import {selectAllCategories} from "../../slices/categoriesSlice";
import {selectAllBudgetCategories} from "../../slices/budgetCategoriesSlice";
import {selectAllTransactions} from "../../slices/transactionsSlice";

const useBudgetApp = () => {
  const transactionCategories = useSelector(selectAllCategories);
  const budgetCategories = useSelector(selectAllBudgetCategories);
  const transactions = useSelector(selectAllTransactions);

  const periods = useMemo(() => {
    const now = moment().startOf("month");
    const startOfMonth = moment().startOf("month").add(1, "month");

    if (transactions.length === 0) {
      let res = [];

      res.push({
        start: now.format("YYYY-MM-DD"),
        year: now.year(),
        month: now.month(),
        inCategories: [],
        outCategories: [],
        noCategories: [],
        amountFilled: 0,
        amountPlanned: 0,
        amountInFilled: 0,
        amountInPlanned: 0,
        amountOutFilled: 0,
        amountOutPlanned: 0
      });

      return res;
    }

    const budgetCategoriesByCategory = {};
    const transactionsByCategoryPeriod = {};

    const sortedBudgetCategories = budgetCategories.toSorted((a, b) => b.valueDate.localeCompare(a.valueDate));

    sortedBudgetCategories
      .forEach(bc => {
        if (!budgetCategoriesByCategory[bc.category]) {
          budgetCategoriesByCategory[bc.category] = [];
        }

        budgetCategoriesByCategory[bc.category].push(bc);
      });

    transactions
      .filter(t => t.type !== TRANSACTION_TYPE_TRANSFER && !t.future)
      .forEach(t => {
        const category = t.category || "0";
        const period = t.valueDate.substring(0, 8) + "01";
        const key = category + "|" + period;

        if (!transactionsByCategoryPeriod[key]) {
          transactionsByCategoryPeriod[key] = [];
        }

        transactionsByCategoryPeriod[key].push(t);
      });

    const result = [];

    const firstDate = moment(transactions.map(t => t.valueDate).sort((a, b) => a.localeCompare(b))[0]).startOf("month");

    let yearPeriod = {year: null, categories: []};

    while (startOfMonth.isSameOrAfter(firstDate)) {
      const period = {
        start: startOfMonth.clone().format("YYYY-MM-DD"),
        year: startOfMonth.year(),
        month: startOfMonth.month(),
        inCategories: [],
        outCategories: [],
        noCategories: []
      };

      const nonBudgetCategories = ["0"];

      transactionCategories.forEach(category => {
        const budgetCat = (budgetCategoriesByCategory[category.id] || []).find(bc => bc.valueDate <= period.start);

        if (!budgetCat || budgetCat.amount === 0) {
          nonBudgetCategories.push(category.id);
          return;
        }

        const cat = {
          category,
          budgetCat,
          transactions: transactionsByCategoryPeriod[category.id + "|" + period.start] || []
        };

        if (budgetCat.amount > 0) {
          period.inCategories.push(cat);
        } else {
          period.outCategories.push(cat);
        }
      });

      period.noCategories = nonBudgetCategories
        .flatMap(categoryId => transactionsByCategoryPeriod[categoryId + "|" + period.start] || []);

      const noCategoryAmount = period.noCategories.reduce((a, b) => a + (b.amount * b.rate), 0);

      period.amountInFilled = period.inCategories
        .flatMap(c => c.transactions)
        .reduce((a, b) => a + (b.amount * b.rate), 0);

      period.amountInPlanned = period.inCategories
        .map(c => c.budgetCat.amount)
        .reduce((a, b) => a + b, 0);

      period.amountOutFilled = period.outCategories
        .flatMap(c => c.transactions)
        .reduce((a, b) => a + (b.amount * b.rate), 0) + noCategoryAmount;

      period.amountOutPlanned = period.outCategories
        .map(c => c.budgetCat.amount)
        .reduce((a, b) => a + b, 0);

      period.amountFilled = period.amountInFilled + period.amountOutFilled;
      period.amountPlanned = period.amountInPlanned + period.amountOutPlanned;

      if (yearPeriod.year !== period.year) {
        yearPeriod = {
          year: period.year,
          month: -1,
          start: period.year + "-01-01",
          inCategories: [],
          outCategories: [],
          noCategories: [],
          amountInFilled: 0,
          amountInPlanned: 0,
          amountOutFilled: 0,
          amountOutPlanned: 0
        };

        result.push(yearPeriod);
      }

      if (startOfMonth.isSameOrBefore(now)) {
        yearPeriod.inCategories = [...yearPeriod.inCategories, ...period.inCategories];
        yearPeriod.outCategories = [...yearPeriod.outCategories, ...period.outCategories];
        yearPeriod.noCategories = [...yearPeriod.noCategories, ...period.noCategories];
        yearPeriod.amountInFilled += period.amountInFilled;
        yearPeriod.amountInPlanned += period.amountInPlanned;
        yearPeriod.amountOutFilled += period.amountOutFilled;
        yearPeriod.amountOutPlanned += period.amountOutPlanned;

        yearPeriod.amountFilled = yearPeriod.amountInFilled + yearPeriod.amountOutFilled;
        yearPeriod.amountPlanned = yearPeriod.amountInPlanned + yearPeriod.amountOutPlanned;
      }

      result.push(period);

      startOfMonth.subtract(1, "month");
    }

    return result;
  }, [budgetCategories, transactionCategories, transactions]);

  return {periods};
};

export default useBudgetApp;
